import React, { useState } from 'react'
import { Container, Row, Col, ProgressBar, Badge } from 'react-bootstrap'
import { CheckCircle, Target, MapPin, Zap, Download } from 'lucide-react'
import { ViewContainer, ScrollableContent, PageHeader } from './styles'
import { ObjectSelectionStep, LocationSelectionStep, ChallengesStep, GenerateStep } from './steps'
import { Step, StepData, ObjectGroup, MilitaryObject } from './types'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import styled from 'styled-components'

const StepsContainer = styled.div`
  background: #2a2a2a;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid #333;
`

const StepIndicator = styled.div<{ $active: boolean; $completed: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: ${(props) => {
    if (props.$completed) return 'rgba(40, 167, 69, 0.1)'
    if (props.$active) return 'rgba(128, 0, 133, 0.1)'
    return 'transparent'
  }};
  border: 1px solid
    ${(props) => {
      if (props.$completed) return 'rgba(40, 167, 69, 0.3)'
      if (props.$active) return 'rgba(128, 0, 133, 0.3)'
      return 'transparent'
    }};

  .step-icon {
    color: ${(props) => {
      if (props.$completed) return '#28a745'
      if (props.$active) return '#800085'
      return '#666'
    }};
  }

  .step-text {
    color: ${(props) => {
      if (props.$completed) return '#28a745'
      if (props.$active) return '#fff'
      return '#888'
    }};
    font-weight: ${(props) => (props.$active ? '600' : '400')};
  }
`

// ComingSoonPlaceholder moved to individual step components

// Types are now imported from ./types

const STEPS = [
  {
    key: 'objects' as Step,
    title: 'Object Selection',
    icon: Target,
    description: 'Choose military objects to detect',
  },
  {
    key: 'location' as Step,
    title: 'Location',
    icon: MapPin,
    description: 'Select environments and backgrounds',
  },
  {
    key: 'challenges' as Step,
    title: 'Challenges',
    icon: Zap,
    description: 'Add weather, lighting, and conditions',
  },
  {
    key: 'generate' as Step,
    title: 'Generate',
    icon: Download,
    description: 'Configure and generate synthetic data',
  },
]

const SyntheticDataGeneration: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>('objects')
  const [stepData, setStepData] = useState<StepData>({ objects: [], groups: [] })
  const navigate = useNavigate()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)

  const handleStepNext = (data: Partial<StepData>) => {
    setStepData((prev) => ({ ...prev, ...data }))

    // Navigate to next step based on current step
    const currentIndex = STEPS.findIndex((s) => s.key === currentStep)
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1].key)
    }
  }

  const handleStepBack = () => {
    const currentIndex = STEPS.findIndex((s) => s.key === currentStep)
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].key)
    }
  }

  const getStepStatus = (stepKey: Step) => {
    const currentIndex = STEPS.findIndex((s) => s.key === currentStep)
    const stepIndex = STEPS.findIndex((s) => s.key === stepKey)

    if (stepIndex < currentIndex) return 'completed'
    if (stepIndex === currentIndex) return 'active'
    return 'pending'
  }

  const getProgressPercentage = () => {
    const currentIndex = STEPS.findIndex((s) => s.key === currentStep)
    return (currentIndex / (STEPS.length - 1)) * 100
  }

  const renderStepContent = () => {
    const stepProps = {
      stepData,
      onNext: handleStepNext,
      onBack: handleStepBack,
    }

    switch (currentStep) {
      case 'objects':
        return <ObjectSelectionStep {...stepProps} />

      case 'location':
        return <LocationSelectionStep {...stepProps} />

      case 'challenges':
        return <ChallengesStep {...stepProps} />

      case 'generate':
        return <GenerateStep {...stepProps} />

      default:
        return null
    }
  }

  return (
    <ViewContainer>
      <ScrollableContent>
        <Container fluid>
          <PageHeader>
            <h1>Synthetic Data Generation</h1>
            <p>
              Create realistic synthetic training data with military objects in various environments
              and conditions. Follow the 4-step process to generate high-quality synthetic datasets.
            </p>
          </PageHeader>

          <StepsContainer>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="text-white mb-0">Generation Workflow</h5>
              <Badge bg="primary">{Math.round(getProgressPercentage())}% Complete</Badge>
            </div>

            <ProgressBar
              now={getProgressPercentage()}
              className="mb-4"
              style={{ height: '6px', background: '#333' }}
            />

            <Row>
              {STEPS.map((step, index) => {
                const status = getStepStatus(step.key)
                const isCompleted = status === 'completed'
                const isActive = status === 'active'

                return (
                  <Col key={step.key} md={3} className="mb-2">
                    <StepIndicator $active={isActive} $completed={isCompleted}>
                      {isCompleted ? (
                        <CheckCircle size={20} className="step-icon" />
                      ) : (
                        <step.icon size={20} className="step-icon" />
                      )}
                      <div>
                        <div className="step-text">
                          <strong>
                            Step {index + 1}: {step.title}
                          </strong>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#888' }}>{step.description}</div>
                      </div>
                    </StepIndicator>
                  </Col>
                )
              })}
            </Row>

            {(stepData.objects.length > 0 || stepData.groups.length > 0) && (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid #333' }}>
                <div className="text-muted">
                  {stepData.groups.length > 0 && (
                    <div className="mb-1">
                      <strong>Groups:</strong>{' '}
                      {stepData.groups
                        .map((group) => `${group.name} (${group.objects.length})`)
                        .join(', ')}
                    </div>
                  )}
                  <div>
                    <strong>Total Objects:</strong> {stepData.objects.length} objects ready for
                    detection training
                  </div>
                </div>
              </div>
            )}
          </StepsContainer>

          {renderStepContent()}
        </Container>
      </ScrollableContent>
    </ViewContainer>
  )
}

export default SyntheticDataGeneration
